/**
 * Career-Compatibility v2 — classical Ashtakoot + modern career layer.
 *
 * Shows: classical compatibility (with parihara checks), career-axis
 * complementarity readings (earning, risk, location, dual-career), and
 * pre-marriage negotiation prompts. Constructive tone throughout —
 * differences framed as complementarities, not mismatches.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  CareerCompatSectionEntry,
  CareerCompatibilityResponse,
  ChartCreateRequest,
  CompatibilityDoshaEntry,
  CompatibilityKootEntry,
  NegotiationPromptEntry,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function CareerCompatPage({ onBack }: { onBack: () => void }) {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [boyChart, setBoyChart] = useState<ChartCreateRequest | null>(null);
  const [girlChart, setGirlChart] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<
    CareerCompatibilityResponse,
    Error,
    { boy: ChartCreateRequest; girl: ChartCreateRequest }
  >({ mutationFn: (req) => api.careerCompatibility(req) });

  const onSubmit = useCallback(() => {
    if (boyChart && girlChart) reportMut.mutate({ boy: boyChart, girl: girlChart });
  }, [boyChart, girlChart, reportMut]);

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    if (reportMut.error instanceof ApiError && reportMut.error.status === 422)
      return t('अमान्य इनपुट', 'Invalid input');
    return reportMut.error.message ?? 'Error';
  }, [reportMut.error, lang]);

  const ready = boyChart != null && girlChart != null;
  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!boyChart || !girlChart) return;
    try {
      setPdfDownloading(true);
      const blob = await api.careerCompatibilityPdfBlob({ boy: boyChart, girl: girlChart }, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `career_compat_${boyChart.name}_${girlChart.name}.pdf`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [boyChart, girlChart, lang, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button" onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('करियर + विवाह संगति', 'Career + Marriage Compatibility')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'अष्टकूट + करियर-संगति का संयुक्त विश्लेषण — पूरकता एवं विवाह-पूर्व संवाद-प्रश्न।',
              'Joint Ashtakoot + career-fit analysis — complementarities and pre-marriage discussion prompts.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'hi'}
          >
            हिन्दी
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'en'}
          >
            English
          </button>
        </div>
      </header>

      <section className="space-y-5">
        <div className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
          <h3 className="mb-2 text-sm font-semibold text-saffron-300">
            1. {t('वर / पुरुष — जन्म विवरण', 'Boy / Groom — Birth Details')}
          </h3>
          <ChartInputForm onSubmit={(req) => setBoyChart(req)} isSubmitting={false} />
          {boyChart && (
            <p className="mt-2 text-xs text-emerald-400">
              ✓ {t('दर्ज', 'Saved')}: {boyChart.name}
            </p>
          )}
        </div>
        <div className="rounded-2xl bg-stone-900/40 p-3 ring-1 ring-stone-800">
          <h3 className="mb-2 text-sm font-semibold text-saffron-300">
            2. {t('वधू / स्त्री — जन्म विवरण', 'Girl / Bride — Birth Details')}
          </h3>
          <ChartInputForm onSubmit={(req) => setGirlChart(req)} isSubmitting={false} />
          {girlChart && (
            <p className="mt-2 text-xs text-emerald-400">
              ✓ {t('दर्ज', 'Saved')}: {girlChart.name}
            </p>
          )}
        </div>
      </section>

      <button
        type="button"
        disabled={!ready || reportMut.isPending}
        onClick={onSubmit}
        className="rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
      >
        {reportMut.isPending
          ? t('विश्लेषण हो रहा है…', 'Analysing…')
          : t('विश्लेषण करें', 'Analyse')}
      </button>

      {submitError && (
        <p className="rounded-2xl bg-rose-950/60 p-4 text-rose-200 ring-1 ring-rose-900">
          {submitError}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

function ReportView({
  report,
  lang,
  t,
  onDownloadPdf,
  pdfDownloading,
}: {
  report: CareerCompatibilityResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  return (
    <article className="space-y-4">
      {/* Top header with PDF download */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('करियर + विवाह संगति', 'Career + Marriage Compatibility')}
          </p>
          <p className="mt-1 text-base font-medium text-stone-200">
            {report.classical.boy_input.name} <span className="text-stone-500">·</span>{' '}
            {report.classical.girl_input.name}
          </p>
          <p className="mt-1 text-[11px] text-stone-500">
            {t('कुल अंक', 'Total points')}: {report.classical.total_points} / {report.classical.max_total}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading
            ? t('बन रहा है…', 'Generating…')
            : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      {/* Combined headline */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <p className="text-base leading-relaxed text-stone-100">
          {lang === 'hi' ? report.headline_hi : report.headline_en}
        </p>
      </section>

      {/* Classical: what works */}
      <section className="rounded-2xl bg-emerald-950/20 p-5 ring-1 ring-emerald-800/40">
        <h2 className="mb-3 text-base font-semibold text-emerald-200">
          {t('विवाह-दृष्टि: क्या अच्छा है', 'Marriage Aspects: What Works')}
        </h2>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.classical.what_works_hi : report.classical.what_works_en).map((w, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-emerald-400">●</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Classical: what to manage (only if any) */}
      {report.classical.what_to_manage_en.length > 0 && (
        <section className="rounded-2xl bg-amber-950/20 p-5 ring-1 ring-amber-800/40">
          <h2 className="mb-3 text-base font-semibold text-amber-200">
            {t('विवाह-दृष्टि: किसका ध्यान रखें', 'Marriage Aspects: What to Manage')}
          </h2>
          <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
            {(lang === 'hi' ? report.classical.what_to_manage_hi : report.classical.what_to_manage_en).map((w, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-400">●</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Modern career layer */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('करियर-संगति विश्लेषण', 'Career Compatibility')}
        </h2>
        <p className="mb-4 text-sm leading-relaxed text-stone-200">
          {lang === 'hi' ? report.modern.headline_hi : report.modern.headline_en}
        </p>
        <div className="space-y-3">
          {report.modern.sections.map((s) => (
            <CareerSectionCard section={s} lang={lang} key={s.axis_id} t={t} />
          ))}
        </div>
      </section>

      {/* Negotiation prompts */}
      <section className="rounded-2xl bg-sky-950/20 p-5 ring-1 ring-sky-800/40">
        <h2 className="mb-1 text-base font-semibold text-sky-200">
          {t('विवाह-पूर्व संवाद-प्रश्न', 'Pre-Marriage Discussion Topics')}
        </h2>
        <p className="mb-4 text-xs text-stone-400">
          {t(
            'इन विषयों पर खुला संवाद विवाह की नींव को सशक्त बनाता है।',
            'Open conversation on these topics strengthens the foundation of marriage.',
          )}
        </p>
        <div className="space-y-3">
          {report.modern.negotiation_prompts.map((p, i) => (
            <PromptCard prompt={p} lang={lang} key={i} />
          ))}
        </div>
      </section>

      {/* Decade outlook */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('दशक-दृष्टि', 'Decade Outlook')}
        </h2>
        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
              {report.classical.boy_input.name}
            </p>
            <ul className="space-y-1 text-xs text-stone-200">
              {(lang === 'hi' ? report.modern.boy_career_decade_hi : report.modern.boy_career_decade_en).map((d, i) => (
                <li key={i}>· {d}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
              {report.classical.girl_input.name}
            </p>
            <ul className="space-y-1 text-xs text-stone-200">
              {(lang === 'hi' ? report.modern.girl_career_decade_hi : report.modern.girl_career_decade_en).map((d, i) => (
                <li key={i}>· {d}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-3 border-t border-stone-800/60 pt-3 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.modern.joint_decade_verdict_hi : report.modern.joint_decade_verdict_en}
        </p>
      </section>

      {/* Per-koot detail (collapsible) */}
      <details className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <summary className="cursor-pointer text-base font-semibold text-saffron-200">
          {t('अष्टकूट विस्तार (विस्तार)', 'Eight-Koot Detail (expand)')}
        </summary>
        <div className="mt-3 space-y-2">
          {report.classical.koots.map((k) => (
            <KootRow koot={k} lang={lang} key={k.koot_id} />
          ))}
        </div>
      </details>

      {/* Doshas (only fired ones) */}
      {report.classical.doshas.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-1 text-base font-semibold text-saffron-200">
            {t('दोष-विश्लेषण एवं परिहार', 'Dosha Analysis & Pariharas')}
          </h2>
          <div className="space-y-3 mt-3">
            {report.classical.doshas.map((d) => (
              <DoshaRow d={d} lang={lang} t={t} key={d.dosha_id} />
            ))}
          </div>
        </section>
      )}

      {/* Joint path */}
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <h2 className="mb-2 text-base font-semibold text-saffron-200">
          {t('संयुक्त पथ', 'Joint Path')}
        </h2>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.classical.joint_path_hi : report.classical.joint_path_en}
        </p>
      </section>

      {/* Methodology */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.classical.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">{lang === 'hi' ? m.method_hi : m.method_en}</span>
              <span className="text-stone-600"> — {lang === 'hi' ? m.source_hi : m.source_en}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.classical.disclaimer_hi : report.classical.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function CareerSectionCard({
  section,
  lang,
  t,
}: {
  section: CareerCompatSectionEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <div className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2.5">
      <p className="text-sm font-medium text-saffron-300">
        {lang === 'hi' ? section.label_hi : section.label_en}
      </p>
      {section.boy_reading_en !== '—' && (
        <div className="mt-1 grid gap-1 text-xs text-stone-300 sm:grid-cols-2">
          <p>
            <span className="text-stone-500">{t('पुरुष', 'Boy')}:</span>{' '}
            {lang === 'hi' ? section.boy_reading_hi : section.boy_reading_en}
          </p>
          <p>
            <span className="text-stone-500">{t('स्त्री', 'Girl')}:</span>{' '}
            {lang === 'hi' ? section.girl_reading_hi : section.girl_reading_en}
          </p>
        </div>
      )}
      <p
        className="mt-2 text-sm leading-relaxed text-stone-100"
        dangerouslySetInnerHTML={{
          __html: (lang === 'hi' ? section.complementarity_hi : section.complementarity_en)
            .replace(/\*\*(.+?)\*\*/g, '<strong class="text-saffron-200">$1</strong>'),
        }}
      />
    </div>
  );
}

function PromptCard({
  prompt,
  lang,
}: {
  prompt: NegotiationPromptEntry;
  lang: 'hi' | 'en';
}) {
  return (
    <details className="rounded-md border-l-2 border-sky-600/60 bg-stone-900/40 px-3 py-2.5">
      <summary className="cursor-pointer text-sm font-medium text-sky-200">
        {lang === 'hi' ? prompt.topic_hi : prompt.topic_en}
      </summary>
      <ul className="mt-2 space-y-1 text-xs leading-relaxed text-stone-200">
        {(lang === 'hi' ? prompt.questions_hi : prompt.questions_en).map((q, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-sky-400">?</span>
            <span>{q}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function KootRow({
  koot,
  lang,
}: {
  koot: CompatibilityKootEntry;
  lang: 'hi' | 'en';
}) {
  const colour = {
    strong:     'border-emerald-600/60',
    moderate:   'border-sky-600/60',
    attention:  'border-amber-600/60',
    needs_work: 'border-rose-600/60',
  }[koot.strength];
  return (
    <div className={'rounded-md border-l-2 bg-stone-900/30 px-3 py-2 ' + colour}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium text-saffron-300">
          {lang === 'hi' ? koot.name_hi : koot.name_en}
        </p>
        <p className="text-[10px] text-stone-500">
          {koot.awarded.toFixed(0)}/{koot.max_points}
        </p>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-stone-100">
        {lang === 'hi' ? koot.why_works_hi : koot.why_works_en}
      </p>
    </div>
  );
}

function DoshaRow({
  d,
  lang,
  t,
}: {
  d: CompatibilityDoshaEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const colour = d.parihara_applies
    ? 'border-emerald-600/60'
    : 'border-amber-600/60';
  return (
    <div className={'rounded-md border-l-2 bg-stone-900/30 px-3 py-2.5 ' + colour}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-saffron-300">
          {lang === 'hi' ? d.name_hi : d.name_en}
        </p>
        {d.parihara_applies && (
          <span className="text-[10px] text-emerald-400">
            ✓ {t('परिहार', 'Parihara')}
          </span>
        )}
      </div>
      {d.parihara_applies && (
        <p className="mt-1 text-xs italic text-emerald-200">
          {lang === 'hi' ? d.parihara_reason_hi : d.parihara_reason_en}
        </p>
      )}
      <ul className="mt-2 space-y-1 text-xs leading-relaxed text-stone-200">
        {(lang === 'hi' ? d.management_steps_hi : d.management_steps_en).map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-saffron-400">●</span>
            <span>{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
